import { t } from '../../shared/i18n/index.js';

/**
 * What signing out would lose, named one by one: changes made on this device
 * that the server does not have. Signing out removes the account's data
 * from this device and keeps nothing for later (maps on this device are not
 * the account's: they stay). Computed after the last sync attempt.
 *
 * @param {object} input
 * @param {Array<{docId: string, title?: string, _storage?: string}>} input.docs  the vault's maps
 * @param {(id: string) => 'dirty'|'clean'|'unknown'} input.stateOf  the dirty bits (unsynced-changes.js)
 * @param {string|null} [input.vaultId]  the account vault's document
 * @param {boolean} [input.vaultConfirmed]  vault connected, synced, nothing pending: an
 *   untracked vault then counts as on the server
 * @param {((docIds: string[]) => Promise<{kept: string[], unchecked: string[]}>)|null} [input.checkWithServer]
 *   untracked maps compared with the server; null when it can't be reached
 * @param {(docId: string) => Promise<boolean>} [input.hasCopy]  a copy of the map on this device
 * @param {(docId: string) => boolean} [input.quotaRefused]  the server refused its changes (account full)
 * @param {(docId: string) => boolean} [input.paused]  its sync is paused
 * @param {Map<string, string[]>} [input.waitingFiles]  map → names of files attached offline, not sent
 * @param {boolean} [input.storageBroken]  this device could not save some changes
 * @returns {Promise<string[]>} one line per loss; empty when nothing is lost
 */
export async function signOutLosses({
  docs = [], stateOf, vaultId = null, vaultConfirmed = false, checkWithServer = null,
  hasCopy = async () => true, quotaRefused = () => false, paused = () => false,
  waitingFiles = new Map(), storageBroken = false,
}) {
  const titleOf = (docId) => docs.find((doc) => doc.docId === docId)?.title || t('common.untitled');
  const mapLine = (docId) => {
    const title = titleOf(docId);
    if (quotaRefused(docId)) return t('account.signOutLoss.mapFull', { title });
    if (paused(docId)) return t('account.signOutLoss.mapPaused', { title });
    return t('account.signOutLoss.map', { title });
  };
  const lines = [];
  const untracked = [];
  for (const { docId } of docs.filter((doc) => doc._storage === 'online')) {
    const state = stateOf(docId);
    if (state === 'dirty') lines.push(mapLine(docId));
    else if (state === 'unknown' && await hasCopy(docId)) untracked.push(docId);
  }
  if (untracked.length) {
    const checked = checkWithServer ? await checkWithServer(untracked) : { kept: [], unchecked: untracked };
    for (const docId of checked.kept) lines.push(mapLine(docId));
    for (const docId of checked.unchecked) lines.push(t('account.signOutLoss.mapUnchecked', { title: titleOf(docId) }));
  }
  const vault = vaultId ? stateOf(vaultId) : 'clean';
  if (vault === 'dirty' || (vault === 'unknown' && !vaultConfirmed)) lines.push(t('account.signOutLoss.vault'));
  for (const [docId, names] of waitingFiles) {
    if (names.length) lines.push(t('account.signOutLoss.files', { count: names.length, title: titleOf(docId), names: names.join(', ') }));
  }
  if (storageBroken) lines.push(t('account.signOutLoss.storage'));
  return lines;
}
